import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, RefreshCw, Radio, Stethoscope, ConciergeBell } from "lucide-react";
import { toast } from "sonner";
import useQueueBoard from "../hooks/useQueueBoard";
import DoctorQueueView from "../components/queue/DoctorQueueView";
import ReceptionQueueView from "../components/queue/ReceptionQueueView";

/**
 * One queue engine, two views.
 *  - doctor  → consultation control (call/start/skip/recall/complete)
 *  - reception → check-in, queue prep, doctor assignment, payment collection
 * Admins get both and can toggle. Updates arrive over SSE (see useQueueBoard).
 */
export default function Queue() {
  const { user } = useAuth();
  const isDoctor = user?.role === "doctor";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [view, setView] = useState(isDoctor ? "doctor" : "reception");
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [defaultFee, setDefaultFee] = useState(500);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctors");
        setDoctors(data || []);
        setDoctorId(isDoctor ? user.user_id : data?.[0]?.user_id || "");
      } catch (e) {
        toast.error("Failed to load doctors");
      }
      try {
        const { data } = await api.get("/clinic/settings");
        if (data?.default_consultation_fee) setDefaultFee(data.default_consultation_fee);
      } catch (e) {
        /* optional */
      }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const scope = view === "doctor" ? "doctor" : "clinic";
  const { board, loading, live, refresh } = useQueueBoard({
    scope,
    doctorId: view === "doctor" ? doctorId : null,
    enabled: !!user,
  });

  const activeDoctorName = doctors.find((d) => d.user_id === doctorId)?.name || user?.name;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto fade-in" data-testid="queue-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2 flex items-center gap-2">
            <Radio className={`w-3.5 h-3.5 ${live ? "text-emerald-600" : "text-slate-400"}`} />
            {live ? "Live" : "Syncing"}
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Today&apos;s queue
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {view === "doctor"
              ? "Call, consult and complete — billing stays with reception."
              : "Check in patients, manage the order and collect payments."}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="queue-view-toggle">
              <button
                type="button"
                onClick={() => setView("reception")}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  view === "reception" ? "bg-blue-50 text-blue-700" : "bg-white text-slate-500 hover:bg-slate-50"
                }`}
                data-testid="queue-view-reception"
              >
                <ConciergeBell className="w-4 h-4" /> Reception
              </button>
              <button
                type="button"
                onClick={() => setView("doctor")}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-l border-slate-200 transition-colors ${
                  view === "doctor" ? "bg-emerald-50 text-emerald-700" : "bg-white text-slate-500 hover:bg-slate-50"
                }`}
                data-testid="queue-view-doctor"
              >
                <Stethoscope className="w-4 h-4" /> Doctor
              </button>
            </div>

          {view === "doctor" && doctors.length > 1 && (
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger className="w-48 h-10" data-testid="queue-doctor-select">
                <SelectValue placeholder="Doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="refresh-queue-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {loading && !board ? (
        <div className="p-10 flex justify-center" data-testid="queue-loading">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : view === "doctor" ? (
        <DoctorQueueView
          board={board}
          doctorId={doctorId}
          doctorName={activeDoctorName}
          onChanged={refresh}
        />
      ) : (
        <ReceptionQueueView
          board={board}
          doctors={doctors}
          defaultFee={defaultFee}
          canManage={isAdmin || user?.role === "receptionist"}
          onChanged={refresh}
        />
      )}

      {board?.queue_date && (
        <div className="mt-8 flex items-center justify-center">
          <Badge variant="outline" className="text-xs text-slate-400 border-slate-200" data-testid="queue-date-badge">
            Queue for {board.queue_date} · updates {live ? "live" : "every few seconds"}
          </Badge>
        </div>
      )}
    </div>
  );
}
